import React from "react"
import PropTypes from "prop-types"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import BookingsActionAlert from "ui/Blackout/BookingsActionAlert"
import { getDayChipProps, resolveChipLabel } from "pagesV2/pro/Calendar/blackoutUtils"
import styles from "./index.module.css"

/**
 * The full-width bar above the day grid.
 *
 * Red when the day is fully closed, dark for a partial closure or a crew-off day, light for a
 * resource conflict. The action pill is the same one the day cards carry, so the wording — and
 * what it does when the feed sends a flag instead of a number — stays in one place.
 */
const BlackoutBanner = ({ dayBlackout, onViewDetails, onManageReservations }) => {
  const { t } = useTranslation()
  const chip = getDayChipProps(dayBlackout)

  if (!chip) return null

  const count = dayBlackout.bookings_requiring_action || 0
  const countSuffix = chip.showCount && chip.count > 1 ? ` (${chip.count})` : ""

  return (
    <Flex
      align="center"
      justify="space-between"
      gap="var(--spacing-s)"
      wrap
      className={`${styles.banner} ${styles[chip.variant]}`}
    >
      <Flex align="center" gap="var(--spacing-xs)" className={styles.leading}>
        {/* A closure of the whole business is a warning in its own right, so it carries the
            triangle whether or not any booking needs rescheduling. A lighter closure earns it
            only when there is work to do — the "Crew Off" state in the design has neither.
            `currentColor` because the conflict variant is a light bar with dark text. */}
        {(chip.variant === "full" || Boolean(count)) && (
          <Icon name="AlertTriangle" width={20} height={20} color="currentColor" />
        )}
        <span className={styles.label}>
          {`${resolveChipLabel(chip, t)}${countSuffix}`}
        </span>
        <Button type="primary" size="small" shape="round" onClick={onViewDetails}>
          {t("calendar.blackout.view_details", { defaultValue: "View Details" })}
        </Button>
      </Flex>

      {/* Both halves are gated on the count again. They were briefly unconditional because the
          feed only sent a boolean and the whole group never rendered; `conflicts` now carries a
          real number, so a day with nothing caught offers no jump to an empty list. */}
      {Boolean(count) && (
        <Flex align="center" gap="var(--spacing-s)" wrap>
          <BookingsActionAlert count={count} isCompact />

          {onManageReservations && (
            <Button type="primary" size="small" shape="round" onClick={onManageReservations}>
              {t("calendar.blackout.manage_in_reservations", { defaultValue: "Manage in Reservations Page" })}
            </Button>
          )}
        </Flex>
      )}
    </Flex>
  )
}

BlackoutBanner.propTypes = {
  /** The day's entry from the blackout feed. Renders nothing when the day is clear. */
  dayBlackout: PropTypes.shape({
    status: PropTypes.string,
    bookings_requiring_action: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    blackouts: PropTypes.array,
    conflicts: PropTypes.array
  }),
  onViewDetails: PropTypes.func,
  onManageReservations: PropTypes.func
}

export default BlackoutBanner
